import {
    consoleLogger,
    CoreDI,
    Device,
    IAnimation, inject, injectable,
    IService,
    ObjectBuilder,
    SpineUtils,
    SystemComponentType, Tween, Animation
} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {ReelConstants, ReelsOrdering, SymbolPosition, SymbolsOrdering} from '@gongxigames/framework/slot/utils';
import {WinConfigAnimation, WinTypeConfig} from '@gongxigames/framework/slot/modules/winpresentation/WinConfig';
import {WinPayFilter} from '@gongxigames/framework/slot/modules/winpresentation/WinPayFilter';
import {PayAction} from '@gongxigames/framework/slot/modules/winpresentation/PayAction';
import {PayActionMap} from '@gongxigames/framework/slot/modules/winpresentation/PayActionMap';
import {Pay, Position} from '@gongxigames/framework/slotV2/types/LogicData';
import {ReelsSpeedLevel} from '@gongxigames/framework/slotV2/types';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {SymbolInfo} from '@gongxigames/framework/slotV2/modules/tumbleReels/TumbleReelsService';
import {GridPosition} from '@gongxigames/framework/slotV2/fragments/GridPosition';

@injectable()
export class WinPresentationUtils implements IService {
    public static NAME: symbol = DI.WinPresentationUtils;
    public type: SystemComponentType = 'service';

    @inject(CoreDI.ObjectBuilder)
    protected _objectBuilder!: ObjectBuilder;

    @inject(CoreDI.Settings)
    protected _settings!: SlotSettings;

    @inject(DI.Logic)
    protected _gameLogic!: ILogic;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.SpineUtils)
    protected _spineUtils!: SpineUtils;

    @inject(CoreDI.Device)
    protected _device!: Device;

    /**
     *
     * @param pattern array can include 3 types of elements: number, 'not1|2|3', 'is1|2|3'.
     * E.g. ['10', '10', '10', 'not1|2|3', 'is1|2|3']
     * @param filter checks number of specified symbols
     * @param positions [10, 10, 10, 10, 1]
     * @returns {boolean}
     */
    public patternMatch(pattern: string[], filter: WinPayFilter[], positions: Position[]): boolean {
        for (let i = 0; i < pattern.length; i++) {
            const p = pattern[i];

            if (!positions[i]) {
                break;
            }

            const symbol = positions[i].symbolId;

            if (p.indexOf('is') !== -1) {
                // making regExp /^(1|2|25)$/. Where ${p.split('is')[1]} = 1|2|25
                const regExp = new RegExp(`^(${p.split('is')[1]})$`);

                if (!regExp.test(symbol.toString())) {
                    return false;
                }
            } else if (p.indexOf('not') !== -1) {
                // making regExp /^(?!(?:1|2|25)$)/. Where ${p.split('not')[1]} = 1|2|25
                const regExp = new RegExp(`^(?!(?:${p.split('not')[1]})$)`);

                if (!regExp.test(symbol.toString())) {
                    return false;
                }
            } else if (p.indexOf('*') !== -1) {
                // any symbol
            } else if (p !== symbol.toString()) {
                return false;
            }
        }

        if (filter) {
            for (const f of filter) {
                const count = positions.reduce((n, p) => n + (p.symbolId.toString() === f.symbol ? 1 : 0), 0);

                if (f.count.indexOf(count) === -1) {
                    return false;
                }
            }
        }

        return true;
    }

    public parseWinActions(pays: Pay[]): PayActionMap {
        const winConfig = this.getWinConfig();
        const payActions: PayActionMap = {};

        for (let i = 0; i < pays.length; i++) {
            const pay = pays[i];

            for (const config of winConfig.regular) {
                if (this.patternMatch(config.pattern, config.filter, pay.positions)) {
                    payActions[i] = {
                        animation: config.animation,
                        time: config.time
                    };

                    if (config.framesTime) {
                        payActions[i].framesTime = config.framesTime;
                    }

                    // use first matched pattern
                    break;
                }
            }
        }

        return payActions;
    }

    public createSymbolAnimation(config: PayAction, symbol: number, pos: Position, animationName = ''): IAnimation {
        const position = this.getSymbolPosition(pos.reel, pos.row);
        const animationConfig = this.getAnimationConfig(config, symbol);

        if (!animationConfig) {
            throw new Error(`missing animationConfig for symbols id: ${symbol}`);
        }

        if (animationConfig.spine) {
            return this.createSymbolSpineAnimation(position, animationConfig, animationName);
        } else {
            return this.createSymbolTweenAnimation(position, symbol, animationConfig.animation);
        }
    }

    public createSymbolSpineAnimation(pos: SymbolPosition, animationConfig: WinConfigAnimation, animationName = ''): IAnimation {
        const {spine, cached, skinName} = animationConfig;
        const winAnimationName = animationName ? animationName : animationConfig.animation;
        const animation = this._spineUtils.createAnimation(spine!, winAnimationName, false, cached, 20, skinName);

        animation.container.x += pos.x + ReelConstants.SYMBOL.width / 2;
        animation.container.y += pos.y + ReelConstants.SYMBOL.height / 2;

        return animation;
    }

    public createSymbolTweenAnimation(pos: SymbolPosition, symbol: number, animationName: string): IAnimation {
        let animation;

        if (animationName === 'symbolTweenAnimation') {
            const symbolsSprite = this._objectBuilder.buildSprite(ObjectBuilder.resources.game_sprite.symbols);

            animation = this.createTweenAnimation();
            symbolsSprite.children.forEach((child, i) => child.visible = i === symbol);
            animation.container.addChild(symbolsSprite);
        } else {
            animation = this._objectBuilder.buildAnimation(ObjectBuilder.resources.game_sprite[animationName]);
        }

        animation.container.x += pos.x;
        animation.container.y += pos.y;

        return animation;
    }

    public getAnimationConfig(config: PayAction, symbol: number): any {
        for (const animationConfig of config.animation) {
            if (animationConfig.symbol === '*' || symbol + '' === animationConfig.symbol) {
                return animationConfig;
            }
        }

        return null;
    }

    public getSymbolPosition(reel: number, row: number): SymbolPosition {
        return ReelConstants.REELS[reel].symsPositions[row];
    }

    public getWinTypeConfigForSpinWin(win?: number): WinTypeConfig | undefined {
        const winTypeConfig = this.getSpecialWinTypeConfigBuyFeature(win) || this.getSpecialWinTypeConfig() || this.getWinTypeConfig(win);

        if (winTypeConfig) {
            return winTypeConfig;
        }
    }

    public getSpecialWinTypeConfigBuyFeature(win?: number): WinTypeConfig | undefined {
        if (this._gameLogic.isSpecialWinConfigBuyFeature()) {
            const totalWin = win ?? this._gameLogic.getTotalWin();
            const specialWinConfig = {...this._settings.get('specialWinTypeConfig')!};

            specialWinConfig.maxWinToBetRatio = this._gameLogic.getBuyFeatureMultiplier();

            if (this.winIsMatchesWinConfig(totalWin, specialWinConfig)) {
                return specialWinConfig;
            }
        }
    }

    public getSpecialWinTypeConfig(): WinTypeConfig | undefined {
        const totalWin = this._gameLogic.getTotalWin();
        const specialWinConfig: WinTypeConfig | undefined = this._settings.get('specialWinTypeConfig');

        if (specialWinConfig && this.winIsMatchesWinConfig(totalWin, specialWinConfig)) {
            return specialWinConfig;
        }
    }

    public getWinTypeConfig(win?: number): WinTypeConfig | undefined {
        const winTypeConfigs: WinTypeConfig[] = this.getWinConfig().winTypes;

        if (!win) {
            win = slotStore.logic_win.value;
        }

        for (const config of winTypeConfigs) {
            if (this.winIsMatchesWinConfig(win, config)) {
                return config;
            }
        }
    }

    public isWin(): boolean {
        return slotStore.logic_win.value > 0;
    }

    /**
     * return flow configs depending on mode ('turbo', 'normal')
     * @returns {any}
     */
    public getWinConfig(): any {
        const mode = slotStore.reelsSpeedLevel.value !== ReelsSpeedLevel.normal ? 'turbo' : 'normal';

        if (this._gameConfig.winConfig[mode]) {
            return this._gameConfig.winConfig[mode];
        } else {
            consoleLogger.error(`There is no "${mode}" win config`);

            return this._gameConfig.winConfig.normal;
        }
    }

    public isBigWin(win?: number): boolean {
        if (!this._gameLogic.spinData) {
            return false;
        }

        const bet = slotStore.logic_totalBet.value;

        if (!win) {
            win = slotStore.logic_win.value;
        }

        const ratio = win / bet;
        let threshold = slotStore.big_win_threshold.value;

        if (this._gameLogic.isSpecialWinConfigBuyFeature()) {
            threshold = Math.max(threshold, this._gameLogic.getBuyFeatureMultiplier());
        }

        return ratio >= threshold;
    }

    public getWinSymbols(pays: Pay[]): SymbolInfo[] {
        const winSymbols: SymbolInfo[] = [];

        const isUnique = (position: GridPosition): boolean =>
            !winSymbols.some(symbol => symbol.position.equals(position));

        for (const pay of pays) {
            for (const {reel, row, symbolId} of pay.positions) {
                const position = new GridPosition(reel, row);

                if (isUnique(position)) {
                    winSymbols.push({symbolType: symbolId, position});
                }
            }
        }

        return this.reorderWinSymbols(winSymbols);
    }

    /* Firstly symbols sorted by defaultSymbolsOrder.
    If symbols equal and they placed on the same row - they will be sorted by row order.
    If symbols equal and they on the different reels - they will be sorted by reel order.
     */

    // TODO: refactor
    private reorderWinSymbols(positions: SymbolInfo[]): SymbolInfo[] {
        const winSymbols = [...positions];
        const getOrderIndex = (symbolId: number): number => {
            return this._gameConfig.reelsConfig.defaultSymbolsOrder.findIndex((orderID) => {
                return this.isSymbolsEqual(orderID, symbolId);
            });
        };

        const rowOrder = (a: SymbolInfo, b: SymbolInfo): number => {
            if (ReelConstants.SYMBOLS_ORDERING === SymbolsOrdering.TOP_TO_BOTTOM) {
                return a.position.row - b.position.row;
            } else if (ReelConstants.SYMBOLS_ORDERING === SymbolsOrdering.BOTTOM_TO_TOP) {
                return b.position.row - a.position.row;
            }

            return 0;
        };

        const reelOrder = (a: SymbolInfo, b: SymbolInfo): number => {
            if (ReelConstants.REELS_ORDERING === ReelsOrdering.LEFT_TO_RIGHT) {
                return a.position.reel - b.position.reel;
            }

            return b.position.reel - a.position.reel;
        };

        winSymbols.sort((a, b) => {
            const symbolOrder = getOrderIndex(a.symbolType) - getOrderIndex(b.symbolType);
            const rowOrdering = rowOrder(a, b);

            if (symbolOrder) {
                return symbolOrder;
            } else if (rowOrdering) {
                return rowOrdering;
            }

            return reelOrder(a, b);
        });

        return winSymbols;
    }

    private isSymbolsEqual(orderSymbol: number | number[], rowSymbol: number): boolean {
        if (orderSymbol instanceof Array) {
            return orderSymbol.indexOf(rowSymbol) !== -1;
        }

        return orderSymbol === rowSymbol;
    }

    protected winIsMatchesWinConfig(win: number, winConfig: WinTypeConfig): boolean {
        const bet = slotStore.logic_totalBet.value;
        const ratio: number = win / bet;
        const {maxWinToBetRatio, minWinToBetRatio} = winConfig;
        const inTheRangeWinToBetRatio = maxWinToBetRatio && ratio <= maxWinToBetRatio && ratio >= minWinToBetRatio;
        const withinTheMinimumWinToBetRatio = !maxWinToBetRatio && ratio >= minWinToBetRatio;

        return inTheRangeWinToBetRatio || withinTheMinimumWinToBetRatio;
    }

    private createTweenAnimation(): IAnimation {
        const config = [
            {alpha: 0, time: 250},
            {alpha: 0, time: 150},
            {alpha: 1, time: 250},
            {alpha: 1, time: 350},
            {alpha: 0, time: 250},
            {alpha: 0, time: 150},
            {alpha: 1, time: 250},
            {alpha: 1, time: 350}
        ];

        const animation = new Animation();
        let tweenLast: Tween | undefined;

        animation.container.name = 'TweenAnimation';

        for (const {alpha, time} of config) {

            const tween = new Tween(animation.container)
                .to({alpha}, time)
                .onComplete(() => animation.container.visible = true)
                .start();

            if (tweenLast) {
                tweenLast.chain(tween);
            } else {
                animation.addTween(tween);
            }

            tweenLast = tween;
        }

        return animation;
    }
}
